import {Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import {VendorsService} from '../../../../services/vendors.service';
import Vendors from '../../../../services/vendor.model';
import {VendorDuesService} from '../../../../services/vendorDues.service';
import VendorDuesModel from '../../../../services/vendorDues.model';
import {ItemMasterService} from '../../../../services/itemMaster.service';
import ItemMasterModel from '../../../../services/itemMaster.model';
import {PaymentTransactionService} from '../../../../services/paymentTransaction.service';
import PaymentTransactionModel from '../../../../services/paymentTransaction.model';
import {BusinessEntityService} from '../../../../services/businessEntity.service';
import BusinessEntity from '../../../../services/businessEntity.model';
import {FarmSiteService} from '../../../../services/farmSite.service';
import FarmSiteModel from '../../../../services/farmSite.model';
import {Sidenavaction} from '../../../../../assets/js/application/sidenavaction';
import * as moment from 'moment';
import * as _ from 'lodash';
import {ActivatedRoute} from '@angular/router';
import {ExcelService} from 'src/app/services/excelService';
import {ToastrService} from 'ngx-toastr';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import { BatchLoader } from 'src/app/Utils/batch-loader.util';

declare var $: any;

/**
 * @component PaymentTransactionComponent
 * @selector app-pay-tx-uc
 * @implements {OnInit}
 * @implements {OnDestroy}
 *
 * @description
 * The `PaymentTransactionComponent` manages the display and processing of vendor payment transactions and dues.
 * It provides a comprehensive interface for tracking outstanding payments, recording new transactions, and
 * generating financial reports related to vendor dues.
 *
 * ## Key Responsibilities:
 * 1. **Data Retrieval**: Fetches vendor dues and payment transaction data based on business and farm site selection.
 * 2. **Payment Processing**: Allows users to record payments against outstanding vendor dues, supporting partial or full payments.
 * 3. **Filtering & Searching**: Provides robust filtering capabilities including:
 *    - Business selection (for administrative users).
 *    - Farm site selection.
 *    - Text-based search for Vendor Names and Vendor Sites.
 * 4. **Sorting**: Allows users to sort the transaction list by Vendor Name, Date, or Farm Site.
 * 5. **Pagination/Infinite Scroll**: Implements a batch loading mechanism to handle large datasets efficiently.
 * 6. **Export**: Facilitates the export of vendor due payment reports to Excel.
 * 7. **Role-Based Access**: Adapts data visibility and actions based on user roles (e.g., 'Business Admin', 'Farmer', 'Farm Manager', 'Employee').
 *
 * ## User Roles & Permissions:
 * - **Farm Category Users**:
 *   - **Business Admin / Farmer**: Automatically linked to their business ID. Can view all farm sites associated with the farm.
 *   - **Farm Manager**: Automatically linked to their business ID. Can view only farm sites assigned to them.
 * - **Other Categories (e.g., Employee)**:
 *   - Must search for and select a business entity to view its payment transactions.
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessCategory`: Business context ('Farm').
 * - `businessID`: ID of the logged-in user's business.
 * - `personID`: ID of the logged-in user.
 * - `loginBusinessName`: Used for report headers.
 * - `LoginUserName`: Used for report headers.
 * - `loginPersonMobileNumber`: Used for report headers.
 *
 * ## Data Flow:
 * 1. **Initialization**: `ngOnInit` determines user context and loads initial farm sites or businesses.
 * 2. **Selection**: User selects Business and Farm Site.
 * 3. **Data Fetching**: `getPaymentTransactions` calls `VendorDuesService` to fetch dues.
 * 4. **Display**: Data is loaded into `vendorDues` via `BatchLoader`.
 * 5. **Payment**: User selects a due record, enters payment details in modal, and submits via `updateVendorDuePayment`.
 *
 * ## UI State Management:
 * - **Loading**: `pgLoad` and `responseLoader` (implied) manage loading states.
 * - **Form**: `registerForm` (selection) and `paymentRegisterForm` (payment entry).
 * - **Modals**: Uses Bootstrap modal for payment entry.
 */
@Component({
  selector: 'app-pay-tx-uc',
  templateUrl: './payment-transaction.component.html',
  styleUrls: ['./payment-transaction.component.scss']
})
export class PaymentTransactionComponent implements OnInit, OnDestroy {
  /** @type {Array<Vendors>} List of vendors (unused in current logic). */
  public vends: Array<Vendors>;

  /** @type {Vendors} Single vendor model (unused in current logic). */
  public vend: Vendors;

  /** @type {Array<VendorDuesModel>} The list of vendor dues currently displayed in the UI. */
  public vendorDues: Array<VendorDuesModel>;

  /** @type {VendorDuesModel} Model used for binding filter criteria (business_id, farm_site_id). */
  public vendorDue: VendorDuesModel;

  /** @type {VendorDuesModel} Model for viewing details of a specific due record. */
  public viewDetails: VendorDuesModel;

  /** @type {ItemMasterModel} Item master model (unused in current logic). */
  public itemMasters: ItemMasterModel;

  /** @type {Array<PaymentTransactionModel>} List of payment transactions (unused in current logic). */
  public paymenttransactions: Array<PaymentTransactionModel>;

  /** @type {any} Master list of fetched payment transactions/dues used for filtering. */
  public dupaymenttransactions: any;

  /** @type {PaymentTransactionModel} Model for recording a new payment transaction. */
  public paymenttransaction: PaymentTransactionModel;

  /** @type {Array<BusinessEntity>} List of available business entities for selection. */
  public businessEntities: Array<BusinessEntity>;

  /** @type {Array<FarmSiteModel>} List of farm sites associated with the selected business. */
  public farmsites: Array<FarmSiteModel>;

  /** @type {FarmSiteModel} Selected farm site model (unused in current logic). */
  public farmsite: FarmSiteModel;

  /** @type {VendorDuesModel} Input data model for payment transaction (unused). */
  public paymentTransactionInputData: VendorDuesModel;

  /** @type {VendorDuesModel} Export data model (unused). */
  public paymentTransactionExport: VendorDuesModel;

  /** @type {{} & Array<VendorDuesModel>} Original vendor dues data (unused). */
  public originalvendorDues: {} & Array<VendorDuesModel>;

  /** @type {any} Generic date holder. */
  date: any;

  // pagination sorting
  /** @type {number} Current page number. Default 1. */
  public currentPageNumber: number = 1;

  /** @type {number} Items per page. Default 15. */
  public noOfItemsPerPage: number = 15;

  /** @type {number} Duplicate current page number. Default 1. */
  public dupCurrentpageNum: number = 1;

  /** @type {boolean} Flag for page loading state. */
  public pgLoad: boolean;

  /** @type {string} Role of the logged-in user. */
  public loginPersonType: string;

  /** @type {any} Transaction date for payment entry. */
  public transactionDate: any;

  /** @type {number} Unpaid amount for the selected due record. */
  public unpaidAmount: number;

  /** @type {string} Name of the logged-in user's business. */
  public loginUserBusinessName: string;

  /** @type {string} Category of the logged-in user's business. */
  public loginUserBusinessCategory: string;

  /** @type {boolean} Flag for sorting by Name. */
  public nameAsc: boolean;

  /** @type {boolean} Flag for sorting by Date. */
  public dateAsc: boolean;

  /** @type {string} Search keyword. */
  public search: string;

  /** @type {boolean} Flag indicating if search button clicked. */
  public isSearchClicked: boolean;

  /** @type {boolean} Flag for sorting by Site. */
  public siteAsc: boolean;

  /** @type {boolean} Flag indicating if form submitted (unused). */
  public issubmitted: boolean;

  /** @type {number} Total paid amount for displayed records. */
  public totalPaidAmount: number;

  /** @type {number} Total unpaid amount for displayed records. */
  public totalUnpaidAmount: number;

  /** @type {string} Host name (unused). */
  public hostName: string;

  /** @type {any} Application permissions object. */
  public appPermission: any;

  /** @type {string} Keyword for search operations. */
  public keyword: string;

  /** @type {any} Generic item holder. */
  public item: any;

  /** @type {boolean} Flag to display No Data message. */
  public NoDataMessage: boolean = false;

  /** @type {number} Active page number. */
  public activePageNo: number;

  /** @type {boolean} Flag to clear search input. */
  public clearSearchInput: boolean;

  /** @type {FormGroup} Main form for business/location selection. */
  public registerForm: FormGroup;

  /** @type {FormGroup} Form for recording payment. */
  public paymentRegisterForm: FormGroup;

  /** @type {boolean} Flag for dropdown visibility. */
  public isDropdownOpen = false;

  /** @type {any[]} Filtered options for business dropdown. */
  public filteredOptions: any[] = [];

  /** @type {string} Business search input placeholder/value. */
  public businessSearch: string = 'Enter the business name';

  /** @type {boolean} Flag indicating if main form submitted. */
  public submitted: boolean;

  /** @type {boolean} Flag indicating if payment form submitted. */
  public paymentsubmitted: boolean;

  /** @type {boolean} Flag to disable payment submit button. */
  public paymentsubmitteddisabled: boolean;

  /** @type {boolean} Flag to disable main submit button. */
  public submitButtonDisabled: boolean = false;

  /** @type {string} Current filter type. */
  public filterType: string;

  /** @type {boolean} Flag indicating if filters are applied. */
  public filtersApplied: boolean;

  /** @type {boolean} Flag indicating if submit action triggered. */
  public isSubmitClicked: boolean;

  /** @type {boolean} Flag controlling list loader spinner. */
  public listLoader = false;              // Show listLoader spinner

  /** @type {number} Start index for batch loading. */
  public startIndex = 0;             // Tracks how much has been loaded

  /** @type {boolean} Flag indicating if filtering is active. */
  public isFiltering: boolean = false;

  /** @type {boolean} Flag indicating if list is empty. */
  public noList: boolean = false;

  /** @type {BatchLoader<any>} Batch loader utility instance. */
  private batchLoader: BatchLoader<any>;

  /** @type {FormControl} Control for transaction date in payment form. */
  transactionDateControl = new FormControl(new Date(), Validators.required);

  /** @type {number} Count of form fields. */
  public formFieldCount: number = 0;

  /** @type {boolean} Flag to show payment form modal. */
  public showForm: boolean = false;

  /** @type {Date} Maximum allowed date for date picker. */
  maxDate: Date = new Date();


  /**
   * Constructor for PaymentTransactionComponent.
   * Initializes services and forms.
   *
   * @param {ToastrService} toastrService - Service for toast notifications.
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @param {ExcelService} excelService - Service for Excel export.
   * @param {ActivatedRoute} route - Service for route parameters.
   * @param {FarmSiteService} farmsiteService - Service for farm site operations.
   * @param {BusinessEntityService} busineeEntityService - Service for business entity operations.
   * @param {PaymentTransactionService} paymenttransactionService - Service for payment transactions.
   * @param {VendorsService} vendorService - Service for vendor operations.
   * @param {VendorDuesService} vendorDuesService - Service for vendor dues.
   * @param {ItemMasterService} itemMasterservice - Service for item master operations.
   * @param {Sidenavaction} sidenavaction - Service for side navigation.
   * @param {CommonMethodsService} commonMethodsService - Service for common utilities.
   * @param {LocationStrategy} locationStrategy - Service for location strategy.
   * @param {PreviousRouteService} previousRouteService - Service for previous route tracking.
   */
  constructor(private toastrService: ToastrService, public formBuilder: FormBuilder, public excelService: ExcelService, public route: ActivatedRoute, public farmsiteService: FarmSiteService, public busineeEntityService: BusinessEntityService, public paymenttransactionService: PaymentTransactionService, public vendorService: VendorsService, public vendorDuesService: VendorDuesService, public itemMasterservice: ItemMasterService, public sidenavaction: Sidenavaction,
    public commonMethodsService: CommonMethodsService, private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService) {
    this.paymenttransaction = new PaymentTransactionModel();
    this.vendorDue = new VendorDuesModel();
    // Initialize main selection form
    this.registerForm = this.formBuilder.group({
      businessfield: ['', Validators.required],
      locationField: ['', Validators.required],
    });
    // Initialize payment entry form
    this.paymentRegisterForm = this.formBuilder.group({
      unpaidamount: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
      fromDateField: this.transactionDateControl,
    });
    // Calculate field count after form is built // new
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook called after component initialization.
   * - Sets up previous route.
   * - Retrieves user context.
   * - Initializes data based on user role (Farm vs Other).
   * - Loads farm sites or businesses accordingly.
   */
  public async ngOnInit() {
    try {
      window.scrollTo(0, 0);
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      this.loginPersonType = localStorage.getItem('loginPersonType');
      this.totalPaidAmount = 0;
      this.totalUnpaidAmount = 0;
      this.pgLoad = true;
      this.search = '';
      this.filterType = '';
      this.filtersApplied = false;
      this.isSubmitClicked = false;
      switch (localStorage.getItem('loginBusinessCategory')) {
        case 'Farm':
          this.vendorDue.business_id = localStorage.businessID;
          this.registerForm.get('businessfield').setValidators(null);
          this.registerForm.get('businessfield').updateValueAndValidity();
          switch (this.loginPersonType) {
            case 'Business Admin':
            case 'Farmer':
              this.farmsites = await this.farmsiteService.getFarmeSitesByFarmsId(localStorage.businessID);
              break;
            default:
              this.farmsites = await this.farmsiteService.getFarmeSitesByFarmManagerIdInFarmSpend(localStorage.businessID, localStorage.personID);
              break;
          }
          if (this.farmsites.length === 1) {
            this.vendorDue.farm_site_id = this.farmsites[0].id; // model-binding key
          }
          break;
        default:
          this.businessSearch = undefined;
          this.registerForm.get('businessfield').patchValue('');
          let businesses = await this.busineeEntityService.getAllBusiness();
          this.businessEntities = businesses.filter(obj => {
            return ((obj.businessType && obj.businessType.length > 0 && obj.businessType[0].businessTypeID && obj.businessType[0].businessTypeID != '618a18ac0f339c001e991b08') && (obj.businessType && obj.businessType.length > 0 && obj.businessType[0].businessTypeID && obj.businessType[0].businessTypeID != '62b3fa8998be51001d17a310'));
          });
          this.filteredOptions = this.businessEntities;
          break;
      }
      this.transactionDate = moment(new Date()).format('DD-MMM-YYYY');
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method f
   * @description Getter for registerForm controls.
   * @returns {any} Form controls.
   */
  public get f() {
    return this.registerForm.controls;
  }

  /**
   * @method f1
   * @description Getter for paymentRegisterForm controls.
   * @returns {any} Form controls.
   */
  public get f1() {
    return this.paymentRegisterForm.controls;
  }

  /**
   * @method filterBusinesses
   * @description Filters business entities based on search input.
   * Updates `filteredOptions`.
   * @side-effect Updates `filteredOptions`.
   */
  public filterBusinesses(): void {
    const value = (this.registerForm.get('businessfield')!.value || '') as BusinessEntity | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.businessName.toLowerCase();
    this.filteredOptions = this.businessEntities.filter(o =>
      o.businessName.toLowerCase().includes(searchText)
    );
  }

  /**
   * @method selectOption
   * @async
   * @description Handles business selection from dropdown.
   * Loads farm sites for the selected business.
   * @param {BusinessEntity} business - Selected business entity.
   */
  public async selectOption(business: BusinessEntity) {
    this.registerForm.get('businessfield')!.setValue(business.businessName);
    $('.custom-select label').css({'top': 0});
    this.isDropdownOpen = false;
    this.businessSearch = business.businessName;
    this.vendorDue.business_id = business.id;
    if (this.vendorDue.business_id) {
      this.farmsites = await this.farmsiteService.getFarmeSitesByFarmId(this.vendorDue.business_id);
      if (this.farmsites.length === 1) {
        this.vendorDue.farm_site_id = this.farmsites[0].id; // model-binding key
      }
    }
  }

  /**
   * @method onFiltersApplied
   * @description Applies filters to the vendor dues list.
   * Orchestrates search and filter type logic.
   * Resets batch loader if no filters active.
   * @side-effect Updates `vendorDues`, `isFiltering`, `batchLoader`.
   */
  public onFiltersApplied() {
    this.isFiltering = true;
    this.vendorDues = [];
    const value = this.search && this.search.replace(/[-_ :.]/g, '').toLowerCase();
    if (this.search != '') {
      if (this.filterType != '' && this.filtersApplied) {
        this.applySearchWithFilterType();
        this.unPaidPurchaseAmountCalculation();
      } else {
        this.applySearchFilter(value);
        this.unPaidPurchaseAmountCalculation();
      }
    } else {
      if (this.filterType != '' && this.filtersApplied) {
        this.applyFilterWithFilterType();
        this.unPaidPurchaseAmountCalculation();
      } else {
        this.isFiltering = false;
        this.initializeBatchLoader();
        this.batchLoader.reset();
        this.batchLoader.loadNextBatch(true);
      }
    }
  }

  /**
   * @method applySearchFilter
   * @private
   * @description Filters dues based on search string (Vendor Name, Vendor Site).
   * @param {string} searchValue - Normalized search string.
   */
  private applySearchFilter(searchValue: string) {
    this.vendorDues = this.dupaymenttransactions.filter((vendorDuesObj: any) =>
        ['Vendor.name', 'Vendor.site'].some(property => {
          const nestedPropertyValue = property.split('.').reduce((acc, current) => acc && acc[current], vendorDuesObj);
          return nestedPropertyValue && nestedPropertyValue.replace(/[-_ :.]/g, '').toLowerCase().includes(searchValue);
        })
      );
  }

  /**
   * @method applyFilterWithFilterType
   * @private
   * @description Placeholder for specific filter type logic.
   */
  private applyFilterWithFilterType() {

  }

  /**
   * @method applySearchWithFilterType
   * @private
   * @description Placeholder for combined search and filter type logic.
   */
  private applySearchWithFilterType() {

  }

  /**
   * @method clearFilters
   * @description Clears filters based on type.
   * @param {string} filterFrom - Source of clear action ('search' or default).
   */
  public clearFilters(filterFrom: string) {
    switch (filterFrom) {
      case 'search':
        this.search = '';
        this.onFiltersApplied();
        break;
      default:
        this.filterType = '';
        this.filtersApplied = false;
        this.onFiltersApplied();
        break;
    }
  }

  /**
   * @method getPaymentTransactions
   * @async
   * @description Fetches payment transactions (vendor dues) from server.
   * Validates form, resets state, calls service, initializes batch loader.
   * Calculates totals.
   * @side-effect Updates `dupaymenttransactions`, `vendorDues`, `totalPaidAmount`, `totalUnpaidAmount`.
   */
  public async getPaymentTransactions() {
    this.submitted = true;
    this.registerForm.markAllAsTouched();
    if (this.registerForm.invalid) {
      return false;
    }
    this.submitted = false;
    this.dupaymenttransactions = [];
    this.vendorDues = [];
    this.isSubmitClicked = false;
    this.search = '';
    this.filterType = '';
    this.isFiltering = false;
    try {
      this.pgLoad = false;
      this.submitButtonDisabled = true;
      this.dupaymenttransactions = (await this.vendorDuesService.getVendorDuesByBusinessId(this.vendorDue.business_id, this.vendorDue.farm_site_id)).filter(vendorDue => vendorDue && vendorDue.unpaid_amount != 0);
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader.reset();     // Clear state
      await this.batchLoader.loadNextBatch(true); // Load first batch
      this.unPaidPurchaseAmountCalculation();
      this.submitted = false;    // Reset validation flag
      this.registerForm.markAsPristine(); // optional - clears dirty flags
      this.registerForm.markAsUntouched(); // optional - clears touched state
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    } finally {
      this.pgLoad = true;
      this.submitButtonDisabled = false;
      this.isSubmitClicked = true;
    }
  }

  /**
   * @method onScroll
   * @description Host listener for window scroll to trigger infinite loading.
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  private scrollTimeout: any;

  /**
   * @method checkScrollAndLoad
   * @private
   * @description Debounced scroll handler to load next batch of data.
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 &&
        !this.batchLoader?.isLoading() && !this.isFiltering) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * @method initializeBatchLoader
   * @private
   * @description Initializes BatchLoader with data source and callbacks.
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.dupaymenttransactions,
      (updatedList) => {
        this.vendorDues = updatedList;
        this.unPaidPurchaseAmountCalculation();
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * @method unPaidPurchaseAmountCalculation
   * @description Calculates total paid and unpaid amounts for the current list of vendor dues.
   * @side-effect Updates `totalPaidAmount`, `totalUnpaidAmount`.
   */
  public unPaidPurchaseAmountCalculation() {
    this.totalPaidAmount = 0;
    this.totalUnpaidAmount = 0;

    try {
      for (let i = 0; i < this.vendorDues.length; i++) {
        const item = this.vendorDues[i];
        if (item) {
          this.totalPaidAmount += item.paid_amount || 0;
          this.totalUnpaidAmount += item.unpaid_amount || 0;
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }

  }


  /**
   * @method addPaymentTransaction
   * @description Prepares the payment transaction model for a selected vendor due.
   * Opens the payment form modal.
   * @param {VendorDuesModel} vendorDues - Selected vendor due record.
   */
  public addPaymentTransaction(vendorDues: VendorDuesModel) {
    this.showForm = true;
    this.paymenttransaction.id = vendorDues.id;
    this.paymenttransaction.vendor_id = vendorDues.vendor_id;
    this.paymenttransaction.business_id = vendorDues.business_id;
    this.paymenttransaction.farm_site_id = vendorDues.farm_site_id;
    this.paymenttransaction.purchase_amount = vendorDues.purchase_amount;
    this.unpaidAmount = vendorDues.unpaid_amount;
    this.paymenttransaction.vendor_payment_transaction_id = vendorDues.VendorPaymentTransactions.id;
  }

  /**
   * @method updateVendorDuePayment
   * @async
   * @description Submits the payment transaction.
   * Validates form, checks payment amount validity, calls service to update.
   * @side-effect Updates payment, shows toast, refreshes data.
   */
  public async updateVendorDuePayment() {
    this.paymentsubmitted = true;
    if (this.paymentRegisterForm.invalid) {
      return;
    }
    this.paymentsubmitteddisabled = true;
    try {
      this.transactionDate = moment(this.transactionDateControl.value).format('YYYY-MM-DD')
      let dt = new Date();
      let dtime = dt.getHours() + ':' + dt.getMinutes() + ':' + dt.getSeconds();
      this.transactionDate += ' ' + dtime;
      if (!this.transactionDate) {
        this.toastrService.error('Please select date');
        return false;
      }
      this.paymenttransaction.date = this.transactionDate;
      this.paymenttransaction.paid_amount = Number(this.paymenttransaction.paid_amount);
      if (this.paymenttransaction.paid_amount == this.unpaidAmount) {
        this.paymenttransaction.payment_status = 'Fully';
      } else if (this.paymenttransaction.paid_amount < this.unpaidAmount) {
        this.paymenttransaction.payment_status = 'Partially';
      } else if (this.paymenttransaction.paid_amount > this.unpaidAmount) {
        this.toastrService.error('Paid amount should be less than or equal to unpaid amount');
        return false;
      }
      this.paymenttransaction = await this.paymenttransactionService.updateVendorDuesPayment(this.paymenttransaction);
      $('#exampleModalCenter').modal('hide');
      this.paymentsubmitteddisabled = false;
      this.toastrService.success('Payment transaction updated successfully.', '', {
        timeOut: 3000
      });
      this.transactionDate = moment(new Date()).format('DD-MMM-YYYY');
      this.handelSuccessData();
      this.clear();
      // await this.ngOnInit();
      this.paymenttransaction.date = moment(new Date()).format('DD-MMM-YYYY');
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.paymentsubmitteddisabled = false;
    }
  }

  /**
   * @method handelSuccessData
   * @description Refreshes payment transactions list after successful update.
   * @side-effect Calls `getPaymentTransactions`.
   */
  public handelSuccessData() {
    if (this.vendorDue.business_id) {
      this.getPaymentTransactions();
    }
    if (this.vendorDue.business_id) {
      // this.getPaymentTransactionByBidSid();
    }
  }

  /**
   * @method clear
   * @description Resets payment transaction model and form state.
   * @side-effect Resets `paymenttransaction`, `paymentRegisterForm`.
   */
  public clear() {
    this.paymenttransaction = new PaymentTransactionModel();
    this.paymenttransaction.paid_amount = undefined;
    this.paymenttransaction.paid_date = undefined;
    this.paymentsubmitted = false;
    this.showForm = false;
    this.paymentRegisterForm.markAsPristine(); // Reset state
    this.paymentRegisterForm.markAsUntouched();
  }

  /**
   * @method viewDetailsBySelectedRow
   * @description Finds and sets view details for a selected vendor due ID.
   * @param {string} id - Vendor due ID.
   * @returns {VendorDuesModel} Found vendor due record.
   */
  public viewDetailsBySelectedRow(id: string) {
    this.viewDetails = this.vendorDues.find(paymentRegister => paymentRegister && paymentRegister.id === id);
    console.log(409, this.viewDetails)
    return this.viewDetails;
  }

  /**
   * @method initializedatePicker
   * @description Initializes jQuery datepicker for start date.
   * Note: This seems to be legacy jQuery code mixed with Angular.
   */
  public initializedatePicker() {
    let strArray = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let contract_dates = $('#startdate').datepicker({
      maxDate: new Date(),
      onSelect: function(selectedDate) {
        let parsedDate = (selectedDate.split('/')[1] + '-' + strArray[(parseInt(selectedDate.split('/')[0]) - 1)] + '-' + selectedDate.split('/')[2]);
        let option = this.id == 'startdate' ? 'minDate' : 'maxDate',
          instance = $(this).data('datepicker'),
          date = $.datepicker.parseDate(instance.settings.dateFormat || $.datepicker._defaults.dateFormat, selectedDate, instance.settings);
        contract_dates.not(this).datepicker('option', option, date);
        $('#startdate').val(parsedDate);
      }
    });
  }

  /**
   * @method sortingTransaction
   * @description Sorts vendor dues list by Name, Date, or Site.
   * Toggles sort direction.
   * @param {string} type - Sort criteria ('name', 'date', 'site').
   */
  public sortingTransaction(type: string) {
    if (type == 'name') {
      this.nameAsc = !this.nameAsc;
      let orderbyval: 'asc' | 'desc' = 'desc';
      if (this.nameAsc == true) {
        orderbyval = 'asc';
      }
      this.vendorDues = _.orderBy(this.vendorDues, ['Vendor.name'], [orderbyval]);
      this.dateAsc = false;
      this.siteAsc = false;
    }
    if (type == 'date') {
      this.dateAsc = !this.dateAsc;
      let orderbyval: 'asc' | 'desc' = 'desc';
      if (this.dateAsc == true) {
        orderbyval = 'asc';
      }
      this.vendorDues = _.orderBy(this.vendorDues, ['as_of_date'], [orderbyval]);
      this.nameAsc = false;
      this.siteAsc = false;
    }
    if (type == 'site') {
      this.siteAsc = !this.siteAsc;
      let orderbyval: 'asc' | 'desc' = 'desc';
      if (this.siteAsc == true) {
        orderbyval = 'asc';
      }
      this.vendorDues = _.orderBy(this.vendorDues, ['farmSite[0].farmNameAtLocation'], [orderbyval]);
      this.nameAsc = false;
      this.dateAsc = false;
    }
  }

   /**
    * @method
    * @description Toggles the fullscreen mode for the element with ID 'zoomView'.
    * @returns {void} This function does not return any value.
    */
   public openFullScreen(): void {
    let element = document.getElementById('zoomView') as HTMLElement;
    if (element.requestFullscreen) {
      element.requestFullscreen();
    }
  }

  /**
   * @method exportExcel
   * @async
   * @description Exports vendor due payments report to Excel.
   * Constructs request body and calls service to download file.
   */
  public async exportExcel() {
    try {
      const reqBody = {
        "business_id": this.vendorDue.business_id,
        "farm_site_id": this.vendorDue.farm_site_id,
        "order": "new",
        "orderWithUpdatedAt": "true",
        "pageSize": 20000,
        "businessName": this.loginPersonType == 'EMPLOYEE' ? this.businessSearch : localStorage.getItem('loginBusinessName'),
        "farmNameAtLocation": this.farmsites.find((farmSiteObj: FarmSiteModel) => farmSiteObj.id == this.vendorDue.farm_site_id).farmNameAtLocation,
        "personName": localStorage.getItem('LoginUserName'),
        "mobileNumber": localStorage.getItem('loginPersonMobileNumber')
      }
      const fileObj = this.vendorDuesService.excelReportsDownload('vendorDuePayments', reqBody);
      fileObj.subscribe(fileblob => {
        if (fileblob) {
          this.excelService.saveAsExcelFile(fileblob, 'Vendor Due Payments Report');
        }
      });
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

   /**
    * @lifecycle ngOnDestroy
    * @description Lifecycle hook called on component destruction.
    * Cancels ongoing HTTP requests and cleans up batch loader.
    * @side-effect Cancels requests, destroys batch loader.
    */
   public ngOnDestroy(): void {
    this.farmsiteService.cancelOnGoingHttpRequests();
    this.busineeEntityService.cancelOnGoingHttpRequests();
    this.vendorDuesService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }
}
